/* Carries the homepage Custom / Men / Women entry through view, cart, and order.
   Events never include measurements. */
(() => {
  const ALLOWED = new Set(['custom', 'men', 'women']);
  const STORAGE_KEY = 'wcf_entry';
  const script = document.querySelector('script[data-wcf-entry-script]');
  if (script && script.dataset.designMode === 'true') return;
  const customerType = script && script.dataset.customerType === 'account' ? 'account' : 'guest';
  const AUTH_KEY = 'wcf_auth_pending';

  const readEntry = () => {
    try {
      const value = sessionStorage.getItem(STORAGE_KEY) || '';
      return ALLOWED.has(value) ? value : '';
    } catch (error) {
      return '';
    }
  };

  const writeEntry = (entry) => {
    if (!ALLOWED.has(entry)) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, entry);
    } catch (error) {}
  };

  const publish = (eventName, detail) => {
    detail = Object.assign({ customerType }, detail);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, detail));
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        analytics.publish(eventName, detail);
      }
    } catch (error) {}
  };

  let syncedEntry = '';
  const syncCartAttribute = (entry, keepalive) => {
    if (!ALLOWED.has(entry) || entry === syncedEntry) return;
    const url = (window.routes && window.routes.cart_update_url) || '/cart/update';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      keepalive: Boolean(keepalive),
      body: JSON.stringify({ attributes: { _wcf_entry: entry } }),
    })
      .then((response) => {
        if (response.ok) syncedEntry = entry;
      })
      .catch(() => {});
  };

  const stampForm = (form) => {
    const entry = readEntry();
    if (!entry || !(form instanceof HTMLFormElement)) return;
    let input = form.querySelector('input[name="properties[_wcf_entry]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_wcf_entry]';
      form.appendChild(input);
    }
    input.value = entry;
  };

  // Keep legacy option apps from opening their obsolete measurement gate for
  // the theme-owned form. ProductForm remains responsible for every validation.
  const submitOwnedCustomForm = (event) => {
    const button = event.type === 'click' && event.target && event.target.closest
      ? event.target.closest('button[name="add"], input[type="submit"][name="add"]') : null;
    const form = event.type === 'submit' ? event.target : button && button.form;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('[data-wcf-measure]')) return;
    const host = form.closest('product-form');
    const info = host && host.closest('product-info');
    if (!host || typeof host.onSubmitHandler !== 'function' || !info || info.dataset.wcfCustomProduct !== 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    stampForm(form);
    const entry = readEntry();
    if (entry) syncCartAttribute(entry);
    host.onSubmitHandler(event);
  };
  window.addEventListener('click', submitOwnedCustomForm, true);
  window.addEventListener('submit', submitOwnedCustomForm, true);

  // The 3D form owns its Vue handler. Stop legacy app listeners while keeping
  // all quantity, measurement, variant, and in-flight checks in that handler.
  const submitOwned3dForm = (event) => {
    const button = event.type === 'click' && event.target && event.target.closest
      ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
    const form = event.type === 'submit' ? event.target : button && button.form;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-wcf-3d-add') || !form.querySelector('[data-wcf-measure]') || typeof form.wcfSubmitHandler !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    stampForm(form);
    const entry = readEntry();
    if (entry) syncCartAttribute(entry);
    form.wcfSubmitHandler(event, button || event.submitter);
  };
  window.addEventListener('click', submitOwned3dForm, true);
  window.addEventListener('submit', submitOwned3dForm, true);

  document.addEventListener(
    'click',
    (event) => {
      const source = event.target && event.target.closest ? event.target.closest('[data-wcf-entry]') : null;
      if (!source) return;
      const entry = source.getAttribute('data-wcf-entry');
      if (!ALLOWED.has(entry)) return;
      writeEntry(entry);
      publish('wcf_entry_click', { entry });
      syncCartAttribute(entry);
    },
    true
  );

  const trackAuthStart = (action) => {
    if (customerType !== 'guest' || !['register', 'login'].includes(action)) return;
    const detail = { action, entry: readEntry() || 'direct' };
    try { sessionStorage.setItem(AUTH_KEY, JSON.stringify({ ...detail, at: Date.now() })); } catch (error) {}
    publish('wcf_auth_start', detail);
  };
  document.addEventListener('click', (event) => {
    const source = event.target && event.target.closest
      ? event.target.closest('[data-wcf-offer-go], [data-wcf-pdp-offer-go], a[data-wcf-auth-go]') : null;
    if (!source) return;
    const action = source.getAttribute('data-wcf-offer-go') || source.getAttribute('data-wcf-pdp-offer-go') || source.getAttribute('data-wcf-auth-go');
    trackAuthStart(action);
  }, true);

  const trackAuthReturn = () => {
    if (customerType !== 'account') return;
    try {
      const raw = sessionStorage.getItem(AUTH_KEY);
      if (!raw) return;
      sessionStorage.removeItem(AUTH_KEY);
      const pending = JSON.parse(raw);
      const age = Date.now() - Number(pending.at);
      if (!['register', 'login'].includes(pending.action) || age < 0 || age > 30 * 60 * 1000) return;
      publish('wcf_auth_complete', {
        action: 'signed_in',
        initiatedAction: pending.action,
        entry: ALLOWED.has(pending.entry) ? pending.entry : 'direct',
      });
    } catch (error) {}
  };

  window.wcfTrackCouponSelection = (offers) => {
    if (customerType !== 'account') return;
    const kinds = [...new Set((Array.isArray(offers) ? offers : []).map((offer) => offer.kind)
      .filter((kind) => ['threshold', 'pack', 'embroidery'].includes(kind)))];
    if (!kinds.length) return;
    const key = JSON.stringify(kinds.slice().sort());
    try {
      if (sessionStorage.getItem('wcf_coupon_selection_observed') === key) return;
      sessionStorage.setItem('wcf_coupon_selection_observed', key);
    } catch (error) {}
    publish('wcf_coupon_select', { entry: readEntry() || 'direct', kinds, source: 'persisted_account_selection' });
  };

  document.addEventListener(
    'click',
    (event) => {
      const source = event.target && event.target.closest ? event.target.closest('[data-wcf-pdp-offer-go]') : null;
      if (!source) return;
      publish('wcf_pdp_offer', {
        action: source.getAttribute('data-wcf-pdp-offer-go') || 'open',
        entry: readEntry() || 'direct',
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      const isAdd = form.getAttribute('data-type') === 'add-to-cart-form' || action.indexOf('/cart/add') !== -1;
      if (!isAdd) return;
      stampForm(form);
      const entry = readEntry();
      if (entry) syncCartAttribute(entry);
    },
    true
  );

  window.wcfTrackAddToCart = (detail) => {
    const entry = readEntry();
    publish('wcf_add_to_cart', {
      entry: entry || 'direct',
      product_id: detail && detail.productId ? String(detail.productId) : '',
      variant_id: detail && detail.variantId ? String(detail.variantId) : '',
      quantity: detail && Number(detail.quantity) > 0 ? Number(detail.quantity) : 1,
    });
    if (entry) syncCartAttribute(entry);
  };

  const watchImpressions = () => {
    const nodes = Array.from(document.querySelectorAll('[data-wcf-entry]'));
    if (!nodes.length) return;
    const seen = new Set();
    const emit = (node) => {
      const entry = node.getAttribute('data-wcf-entry');
      if (!ALLOWED.has(entry) || seen.has(entry)) return;
      seen.add(entry);
      publish('wcf_hero_impression', { entry: entry });
    };
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(emit);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          emit(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: [0.5] }
    );
    nodes.forEach((node) => observer.observe(node));
  };

  const BROWSER_KEY = 'wcf_measurement_browser';
  const readBrowserMeasurements = () => {
    try {
      const value = localStorage.getItem(BROWSER_KEY) || '';
      return value.length > 4000 ? '' : value.trim();
    } catch (error) {
      return '';
    }
  };

  window.wcfRememberMeasurements = (text) => {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value || value.length > 4000) return;
    try {
      localStorage.setItem(BROWSER_KEY, value);
      window.dispatchEvent(new Event('wcf:browser-measurements-changed'));
    } catch (error) {}
  };

  window.wcfTrackMeasurement = (complete) => {
    publish('wcf_measurement_gate', {
      complete: Boolean(complete),
      entry: readEntry() || 'direct',
    });
  };

  const cartItemsToFormData = (items, sections, sectionsUrl) => {
    const body = new FormData();
    items.forEach((item, index) => {
      body.append('items[' + index + '][id]', String(item.id));
      body.append('items[' + index + '][quantity]', String(item.quantity));
      Object.keys(item.properties || {}).forEach((name) => {
        const value = item.properties[name];
        if (value) body.append('items[' + index + '][properties][' + name + ']', String(value));
      });
    });
    if (sections) body.append('sections', sections);
    if (sectionsUrl) body.append('sections_url', sectionsUrl);
    return body;
  };

  window.wcfCartItemsToFormData = cartItemsToFormData;

  window.wcfBuild3dCartItems = (form) => {
    if (!(form instanceof HTMLFormElement)) return null;
    const data = new FormData(form);
    const properties = {};
    for (const [name, value] of data.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      const text = String(value || '').trim();
      if (match && text) properties[match[1]] = match[1] === 'Design' ? text : text.slice(0, 500);
    }
    const id = String(data.get('id') || '');
    const quantity = Number(data.get('quantity'));
    if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const embroidery = String(properties['Embroidery Text'] || '').slice(0, 40);
    const logo = form.dataset.wcfLogo === '1';
    const units = Number(form.dataset.wcfNativePackSize || 1);
    if (![1, 3, 4].includes(units)) return null;
    const count = quantity * units;
    if (!Number.isSafeInteger(count)) return null;
    const nativePackage = form.dataset.wcfNativePackage === 'true';
    const pieces = [];
    for (let index = 1; index <= (nativePackage ? 1 : quantity); index += 1) {
      const pieceProps = Object.assign({}, properties);
      if (!nativePackage && quantity > 1) pieceProps.Piece = index + ' of ' + quantity;
      if (nativePackage) pieceProps._wcf_customization_units_per_item = String(units);
      pieces.push({ id: id, quantity: nativePackage ? quantity : 1, properties: pieceProps });
    }
    if (!embroidery && !logo) return { items: pieces };
    const group = 'wcf-' + (window.crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
    pieces.forEach((piece) => { piece.properties._wcf_customization_group = group; });
    const fees = [];
    if (embroidery) {
      fees.push({
        id: '50553461375193',
        quantity: count,
        properties: {
          _wcf_customization_fee: 'true',
          _wcf_customization_type: 'Embroidery Service Fee',
          _wcf_customization_group: group,
          _wcf_customization_parent_variant: id,
          'Embroidery text': embroidery,
        },
      });
    }
    if (logo) {
      fees.push({
        id: '50553461539033',
        quantity: count,
        properties: {
          _wcf_customization_fee: 'true',
          _wcf_customization_type: 'Logo Customization Service Fee',
          _wcf_customization_group: group,
          _wcf_customization_parent_variant: id,
        },
      });
    }
    return { items: pieces.concat(fees) };
  };

  window.wcfTrackCustomChoice = (detail) => {
    publish('wcf_custom_choice', {
      entry: readEntry() || 'direct',
      pack: detail && detail.pack ? String(detail.pack).slice(0, 20) : '',
      embroidery: Boolean(detail && detail.embroidery),
      logo: Boolean(detail && detail.logo),
    });
  };

  window.wcfReadMeasureInputs = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    const fields = scope.querySelectorAll('[data-wcf-measure]');
    if (!fields.length) return null;
    const parts = [];
    const missing = [];
    const invalid = [];
    fields.forEach((field) => {
      const label = field.getAttribute('data-wcf-measure-label') || 'Measurement';
      // Number controls can expose an empty value while the user has typed an invalid number.
      if (field.validity?.badInput) {
        invalid.push(label);
        return;
      }
      const value = String(field.value || '').trim().slice(0, 40);
      const required = field.getAttribute('data-wcf-measure-required') !== 'false';
      if (value) {
        if (!/^\d+(?:\.\d+)?$/.test(value) || !Number.isFinite(Number(value)) || Number(value) <= 0) invalid.push(label);
        else parts.push(label + ': ' + value);
      } else if (required) missing.push(label);
    });
    const ok = missing.length === 0 && invalid.length === 0;
    return {
      ok: ok,
      text: parts.join(' | ').slice(0, 500),
      message: ok ? '' : [missing.length ? 'Enter ' + missing.join(', ') + ' before adding to cart.' : '', invalid.length ? 'Use a positive number for ' + invalid.join(', ') + '.' : ''].filter(Boolean).join(' '),
    };
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !document.querySelector('[data-wcf-3d-measures]')) return;
    const action = (form.getAttribute('action') || '') + ' ' + (form.getAttribute('data-type') || '');
    if (action.indexOf('/cart/add') === -1 && action.indexOf('add-to-cart') === -1) return;
    const measure = window.wcfReadMeasureInputs(document.querySelector('[data-wcf-3d-measures]'));
    if (!measure || !measure.ok) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const note = document.querySelector('[data-wcf-3d-measures] [data-wcf-measure-error]');
      if (note) { note.hidden = false; note.textContent = measure ? measure.message : 'Enter measurements before adding to cart.'; }
      window.wcfTrackMeasurement(false);
      return;
    }
    if (!form.hasAttribute('data-wcf-3d-add')) window.wcfTrackMeasurement(true);
    let hidden = form.querySelector('input[name="properties[Measurements]"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[Measurements]';
      form.appendChild(hidden);
    }
    hidden.value = measure.text;
    if (!form.closest('product-info')?.querySelector('[data-wcf-merge-card]') && window.wcfRememberMeasurements) {
      window.wcfRememberMeasurements(measure.text);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.hasAttribute('data-wcf-3d-add')) return;
    if (!document.querySelector('[data-wcf-3d-pack]')) return;
    const action = (form.getAttribute('action') || '') + ' ' + (form.getAttribute('data-type') || '');
    if (action.indexOf('/cart/add') === -1 && action.indexOf('add-to-cart') === -1) return;
    const built = window.wcfBuild3dCartItems(form);
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!built) return;
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      body: window.wcfCartItemsToFormData(built.items),
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => response.json())
      .then((payload) => {
        if (payload && payload.status) return;
        const context = document.querySelector('[data-wcf-product]');
        window.wcfTrackAddToCart({ productId: context && context.getAttribute('data-product-id'), variantId: String(new FormData(form).get('id') || ''), quantity: Number(new FormData(form).get('quantity')) });
        window.wcfTrackCustomChoice({
          pack: form.dataset.wcfPack || '',
          embroidery: form.dataset.wcfEmbroidery === '1',
          logo: form.dataset.wcfLogo === '1',
        });
        return Promise.resolve(window.wcfSyncSelectedDiscounts ? window.wcfSyncSelectedDiscounts() : null).then(() => {
          if (window.wcfTrackBeginCheckout) window.wcfTrackBeginCheckout();
          window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout';
        });
      })
      .catch(() => {});
  }, true);

  const fieldLabel = (field) => {
    const label = field.querySelector('.expo-element-label');
    if (!label) return '';
    const clone = label.cloneNode(true);
    clone.querySelectorAll('.expo-element-required-indicator').forEach((node) => node.remove());
    return clone.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  };

  const measurementValues = (text) => {
    const values = [];
    String(text || '').split('|').map((part) => part.trim()).filter(Boolean).forEach((part) => {
      const index = part.indexOf(':');
      if (index < 1) return;
      values.push([part.slice(0, index).trim().toLowerCase(), part.slice(index + 1).trim()]);
    });
    return values;
  };
  const measurementNameMatches = (left, right) => left === right || left.indexOf(right + ' ') === 0 || right.indexOf(left + ' ') === 0;
  const recognizableMeasurementNames = ['waist', 'chest', 'hips', 'height', 'shoulder', 'shoulder width', 'inseam', 'inner leg length', 'weight'];

  const applyMeasurements = (text) => {
    const values = measurementValues(text);
    let filled = 0;
    const ownedFields = Array.from(document.querySelectorAll('[data-wcf-measure]'));
    if (ownedFields.length) {
      const snapshot = ownedFields.map((control) => {
        const name = String(control.getAttribute('data-wcf-measure-label') || '').trim().toLowerCase();
        const key = String(control.getAttribute('data-wcf-measure-key') || '').trim().toLowerCase();
        const match = name && values.find(([label]) => label === key || measurementNameMatches(label, name));
        return {control, value: match && match[1] ? match[1] : ''};
      });
      // An unrecognized profile must not erase the user's current work. Once a
      // profile is identified, apply its complete snapshot so missing optional
      // or required fields cannot retain measurements from a previous profile.
      if (!snapshot.some(({value}) => value)) return 0;
      snapshot.forEach(({control, value}) => {
        control.value = value;
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
        if (value) filled += 1;
      });
      return filled;
    }
    document.querySelectorAll('.expo-steps .expo-element-wrapper').forEach((field) => {
      const label = fieldLabel(field);
      if (!label) return;
      const match = values.find(([name]) => name === label || label.indexOf(name) === 0 || name.indexOf(label) === 0);
      if (!match || !match[1]) return;
      let wrote = false;
      field.querySelectorAll('input, textarea, select').forEach((control) => {
        if (control.type === 'radio' || control.type === 'checkbox' || control.type === 'file') return;
        control.value = match[1];
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
        wrote = true;
      });
      if (wrote) filled += 1;
    });
    return filled;
  };

  const bindSavedMeasurements = () => {
    const browserBox = document.querySelector('[data-wcf-browser-measurements]');
    if (browserBox && !document.querySelector('[data-wcf-saved-measurements]')) {
      const saved = readBrowserMeasurements();
      const text = browserBox.querySelector('[data-wcf-saved-text]');
      if (saved && text) {
        text.textContent = saved;
        browserBox.hidden = false;
      }
    }
    document.addEventListener('click', (event) => {
      const clear = event.target.closest && event.target.closest('[data-wcf-clear-browser]');
      if (clear) {
        event.preventDefault();
        try {
          localStorage.removeItem(BROWSER_KEY);
        } catch (error) {}
        const box = clear.closest('[data-wcf-browser-measurements]');
        if (box) box.hidden = true;
        return;
      }
      const apply = event.target.closest && event.target.closest('[data-wcf-apply-saved]');
      if (!apply) return;
      event.preventDefault();
      const box = apply.closest('[data-wcf-saved-measurements], [data-wcf-browser-measurements]');
      const text = box && box.querySelector('[data-wcf-saved-text]') ? box.querySelector('[data-wcf-saved-text]').textContent : '';
      const status = box && box.querySelector('[data-wcf-apply-status]');
      const browser = Boolean(box && box.hasAttribute('data-wcf-browser-measurements'));
      const source = browser ? 'browser' : (box && box.getAttribute('data-wcf-measurement-source')) || 'saved';
      const writeStatus = (filled) => {
        if (status) {
          status.hidden = false;
          status.textContent = filled
            ? (browser
              ? 'Measurements added to this form. You can still edit them before adding to cart.'
              : 'Measurements added to this order. You can still edit them. The earlier order will not change.')
            : 'This measurement profile could not be recognized. Your current entries have not been changed. Enter measurements manually or choose another profile.';
        }
        publish('wcf_measurement_reuse', {
          source: source,
          filled: filled > 0,
          entry: readEntry() || 'direct',
        });
      };
      if (!measurementValues(text).some(([label, value]) => value && recognizableMeasurementNames.some((name) => measurementNameMatches(label, name)))) {
        writeStatus(0);
        return;
      }
      const filledNow = applyMeasurements(text);
      if (filledNow > 0 || document.querySelector('[data-wcf-measure]')) {
        writeStatus(filledNow);
        return;
      }
      const trigger = document.querySelector('.expo-steps-trigger-wrap > .expo-steps-trigger');
      if (!document.querySelector('[data-wcf-measure]') && trigger && trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
      window.setTimeout(() => writeStatus(applyMeasurements(text)), 400);
    });
  };

  let checkoutTrackedAt = 0;
  window.wcfTrackBeginCheckout = () => {
    const now = Date.now();
    if (now - checkoutTrackedAt < 1000) return;
    checkoutTrackedAt = now;
    const entry = readEntry();
    publish('wcf_begin_checkout', { entry: entry || 'direct' });
    if (entry) syncCartAttribute(entry, true);
  };
  let checkoutWaiting = false;
  const syncBeforeCheckout = (event, source) => {
    window.wcfTrackBeginCheckout();
    if (!window.wcfSyncSelectedDiscounts) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutWaiting) return;
    checkoutWaiting = true;
    const destination = source && source.href ? source.href : ((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout');
    Promise.resolve(window.wcfSyncSelectedDiscounts()).catch(() => {}).then(() => {
      window.location.href = destination;
    }).finally(() => { checkoutWaiting = false; });
  };
  document.addEventListener(
    'click',
    (event) => {
      const source = event.target && event.target.closest ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]') : null;
      if (!source) return;
      syncBeforeCheckout(event, source);
    },
    true
  );
  document.addEventListener('submit', (event) => {
    if (event.submitter && event.submitter.name === 'checkout') syncBeforeCheckout(event, event.submitter);
  }, true);

  document.addEventListener('change', (event) => {
    const packInput = event.target;
    if (!packInput || packInput.name !== 'properties[Pack]' || !packInput.form) return;
    const size = packInput.value === '4-piece' ? 4 : (packInput.value === '3-piece' ? 3 : 1);
    let quantityInput = packInput.form.elements.namedItem('quantity');
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      packInput.form.appendChild(quantityInput);
    }
    quantityInput.value = String(size);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const group = target.closest('variant-selects fieldset, variant-selects .product-form__input');
    if (!group) return;
    const option = (group.querySelector('legend, .form__label')?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
    const value = String(target.value || '').trim().slice(0, 60);
    if (!value) return;
    const product = document.querySelector('[data-wcf-product]');
    publish('wcf_variant_select', {
      entry: readEntry() || 'direct',
      product_id: product ? product.getAttribute('data-product-id') || '' : '',
      option: option,
      value: value,
    });
  });

  const trackProductView = () => {
    const node = document.querySelector('[data-wcf-product]');
    if (!node) return;
    publish('wcf_product_view', {
      entry: readEntry() || 'direct',
      product_id: node.getAttribute('data-product-id') || '',
      product_handle: node.getAttribute('data-product-handle') || '',
      custom: node.getAttribute('data-wcf-custom') === 'true',
    });
  };

  const start = () => {
    trackAuthReturn();
    watchImpressions();
    trackProductView();
    bindSavedMeasurements();
    const entry = readEntry();
    if (entry) syncCartAttribute(entry);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
